import {
  DepositRequirementSource,
  DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP,
  DepositUnresolvedReason,
  isDepositAmount,
  type DepositBookingContext,
  type DepositCaptureStrategy,
  type DepositPaymentMode,
  type IDepositSettings,
  type IDepositTreatmentConfigurationContribution,
  type IDepositTreatmentOverride,
} from '@principle-theorem/principle-core/interfaces';
import { type DayOfWeek } from '@principle-theorem/shared';
import { DepositAmount } from './deposit-amount';
import { DepositPolicy, type IDepositRequirement } from './deposit-policy';

interface IDepositResolutionBase {
  source: DepositRequirementSource;
  paymentMode?: DepositPaymentMode;
  captureStrategy?: DepositCaptureStrategy;
  treatmentConfigurationDeposits?: Record<
    string,
    IDepositTreatmentConfigurationContribution
  >;
}

export interface IDepositRequired extends IDepositResolutionBase {
  required: true;
  amount: number;
  unresolved?: undefined;
  warning?: undefined;
}

export interface IDepositNotRequired extends IDepositResolutionBase {
  required: false;
  amount?: undefined;
  /**
   * Set when the settings asked for a deposit but the amount could not be
   * worked out. Booking proceeds without one rather than dead-ending the
   * patient; staff surfaces show `warning` so the misconfiguration is visible.
   */
  unresolved?: DepositUnresolvedReason;
  warning?: string;
}

export type IDepositResolution = IDepositRequired | IDepositNotRequired;

export interface IResolveBookingDepositOptions {
  settings?: IDepositSettings;
  treatmentTemplateId?: string;
  treatmentConfigurationIds?: readonly string[];
  /**
   * The context the booking is being made in. Callers must supply the real
   * context: online booking paths always pass `Online`, and staff paths resolve
   * new-vs-existing patient via `StaffDepositBookingContext.resolve`. The patient-facing
   * portal and the Firebase Functions that verify its submissions must agree,
   * or a booking fails with a deposit amount mismatch.
   */
  bookingContext: DepositBookingContext;
  appointmentDay?: DayOfWeek;
  patientDepositRequired?: boolean;
  /**
   * Treatment cost in cents. Used to clamp a deposit so it cannot exceed the
   * value of the treatment being booked; absent means no clamp is applied.
   */
  treatmentCost?: number;
}

/**
 * Resolves whether a booking requires a deposit and how much, gated by the
 * brand's master switch, then the patient flag, then the Treatment Template
 * override and its booking-context applicability.
 */
/** Owns complete monetary resolution for a booking's deposit requirement. */
export class DepositAmountResolver {
  /**
   * Resolves the amount and payment semantics together so callers cannot use a
   * policy decision without its matching charge amount.
   */
  static resolve({
    settings,
    treatmentTemplateId,
    treatmentConfigurationIds,
    bookingContext,
    appointmentDay,
    patientDepositRequired,
    treatmentCost,
  }: IResolveBookingDepositOptions): IDepositResolution {
    const treatmentTemplateOverride = treatmentTemplateId
      ? settings?.treatmentTemplateOverrides?.[treatmentTemplateId]
      : undefined;
    const treatmentConfigurationDeposits =
      DepositAmountResolver._resolveTreatmentConfigurationDeposits(
        settings,
        treatmentConfigurationIds
      );
    const captureStrategy = DepositAmountResolver._resolveCaptureStrategy(
      settings,
      treatmentTemplateOverride
    );
    const paymentMode = captureStrategy
      ? DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP[captureStrategy]
      : undefined;
    const configurationResolution =
      DepositAmountResolver._resolveConfigurationDeposit(
        settings,
        bookingContext,
        treatmentConfigurationDeposits,
        captureStrategy,
        paymentMode
      );
    if (configurationResolution) {
      return configurationResolution;
    }

    const policyResult = DepositPolicy.shouldRequire({
      settings,
      treatmentOverride: treatmentTemplateOverride,
      bookingContext,
      appointmentDay,
      patientDepositRequired,
    });

    return DepositAmountResolver._resolvePolicyAmount(
      policyResult,
      treatmentCost,
      captureStrategy,
      paymentMode
    );
  }

  private static _resolveCaptureStrategy(
    settings?: IDepositSettings,
    treatmentTemplateOverride?: IDepositTreatmentOverride
  ): DepositCaptureStrategy | undefined {
    return (
      (treatmentTemplateOverride?.depositRequired
        ? treatmentTemplateOverride.captureStrategy
        : undefined) ?? settings?.captureStrategy
    );
  }

  private static _resolveConfigurationDeposit(
    settings: IDepositSettings | undefined,
    bookingContext: DepositBookingContext,
    treatmentConfigurationDeposits: Record<
      string,
      IDepositTreatmentConfigurationContribution
    >,
    captureStrategy?: DepositCaptureStrategy,
    paymentMode?: DepositPaymentMode
  ): IDepositResolution | undefined {
    const treatmentConfigurationAmount = Object.values(
      treatmentConfigurationDeposits
    ).reduce(
      (total, contribution) =>
        total + contribution.amountCents * contribution.quantity,
      0
    );
    if (!settings?.enabled || treatmentConfigurationAmount <= 0) {
      return;
    }

    // Configuration deposits outrank template opt-outs and exclusions, but not the blanket booking-context rule.
    if (
      !DepositPolicy.resolveAppliesTo(settings, undefined).includes(
        bookingContext
      )
    ) {
      return {
        required: false,
        source: DepositRequirementSource.BookingContextExcluded,
      };
    }
    return {
      required: true,
      amount: treatmentConfigurationAmount,
      source: DepositRequirementSource.TreatmentConfiguration,
      paymentMode,
      captureStrategy,
      treatmentConfigurationDeposits,
    };
  }

  private static _resolvePolicyAmount(
    policyResult: IDepositRequirement,
    treatmentCost?: number,
    captureStrategy?: DepositCaptureStrategy,
    paymentMode?: DepositPaymentMode
  ): IDepositResolution {
    if (!policyResult.required) {
      return {
        required: false,
        source: policyResult.source,
      };
    }

    const depositConfig = policyResult.amount;

    if (!depositConfig) {
      return {
        required: false,
        source: policyResult.source,
        paymentMode,
        captureStrategy,
        unresolved: DepositUnresolvedReason.MissingDepositAmount,
        warning: 'No deposit amount is configured, so no deposit was taken',
      };
    }

    // Percentage amounts are no longer configurable, but documents saved while
    // they were remain readable, so reject anything the guard no longer accepts
    // rather than treating its value as cents.
    if (!isDepositAmount(depositConfig)) {
      return {
        required: false,
        source: policyResult.source,
        paymentMode,
        captureStrategy,
        unresolved: DepositUnresolvedReason.MissingDepositAmount,
        warning:
          'This deposit amount needs updating before a deposit can be taken',
      };
    }

    return {
      required: true,
      amount: DepositAmount.calculate(depositConfig, treatmentCost ?? 0),
      source: policyResult.source,
      paymentMode,
      captureStrategy,
    };
  }

  private static _resolveTreatmentConfigurationDeposits(
    settings?: IDepositSettings,
    treatmentConfigurationIds?: readonly string[]
  ): Record<string, IDepositTreatmentConfigurationContribution> {
    return (treatmentConfigurationIds ?? []).reduce<
      Record<string, IDepositTreatmentConfigurationContribution>
    >((deposits, configurationId) => {
      const configuredDeposit =
        settings?.treatmentConfigurationDeposits?.[configurationId];
      if (!configuredDeposit) {
        return deposits;
      }
      const existingContribution = deposits[configurationId];
      return {
        ...deposits,
        [configurationId]: {
          amountCents: configuredDeposit.amountCents,
          quantity: (existingContribution?.quantity ?? 0) + 1,
        },
      };
    }, {});
  }
}
